import { Socket } from 'socket.io-client';
import { ResponseError, SocketEventMap } from './types';
import { ChatEvents, EventData } from './enums';

export const emitEvent = <T extends keyof SocketEventMap>(
  socket: Socket | null,
  event: T,
  payload: SocketEventMap[T][EventData.PAYLOAD],
): Promise<SocketEventMap[T][EventData.RESPONSE]> => {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      return reject({ statusCode: 500, message: 'Socket is not connected' });
    }

    let settled = false;
    const finish = (error?: unknown, response?: SocketEventMap[T][EventData.RESPONSE]) => {
      if (settled) return;
      settled = true;
      socket.off('disconnect', onDisconnect);
      if (error) reject(error);
      else resolve(response as SocketEventMap[T][EventData.RESPONSE]);
    };
    const onDisconnect = () =>
      finish({ statusCode: 503, message: 'Socket disconnected', success: false });
    socket.once('disconnect', onDisconnect);

    try {
      socket
        .timeout(15000)
        .emit(
          event,
          payload,
          (
            timeoutError: Error | null,
            err: ResponseError,
            response: SocketEventMap[T][EventData.RESPONSE],
          ) => {
            if (timeoutError)
              return finish({
                statusCode: 504,
                message: 'Socket request timed out',
                success: false,
              });
            if (err) {
              return finish({
                statusCode: err.statusCode || 500,
                message: err.customMessage || 'Unknown error',
                success: false,
              });
            }

            if (
              !response ||
              typeof response !== 'object' ||
              Array.isArray(response) !== (event === ChatEvents.CHECK_ONLINE_STATUS)
            ) {
              return finish({
                statusCode: 500,
                message: 'Received invalid response from server',
              });
            }
            return finish(undefined, response);
          },
        );
    } catch (error) {
      finish(error);
    }
  });
};
